import { Box, Center, Icon, Tooltip, useBreakpointValue, useColorModeValue } from "@chakra-ui/react";
import { useCollectionKey, useHasConversation, useProjectParams, useSidebarNavigation, useUserPreference } from "hooks";
import React, { useCallback, useEffect } from "react";
import { InputBarDropzone } from "screens/conversation/components";
import { ConversationContent } from "./components";
import { useConversationContext } from "./ConversationContext";
import { useDispatch } from "react-redux";
import { downloadConversationById, downloadTags } from "state/conversation/operations";
import { v4 as uuid } from "uuid";
import { ConversationDialogInput } from "./ConversationDialogInput";
import { useCopyValue } from "hooks/useCopies";
import { DEFAULT_SIDEBAR_WIDTH } from "screens/landing/components";
import { BiChat } from "react-icons/bi";

export const ConversationDialog = () => {
  const { projectId, parentRoute } = useProjectParams();
  const projectConversationId = useCollectionKey(projectId || "", "conversationId");
  const { conversationId, setConversationId, isConversationOpen, showConversationMessages, setShowConversationMessages } =
    useConversationContext();
  const currentConversationId = parentRoute === "deal-finder" ? conversationId : projectConversationId || conversationId;
  const hasConversation = useHasConversation(currentConversationId);
  const bgInputColor = useColorModeValue("#fbfbfb", "gray.800");
  const copyTickerPlaceholder = useCopyValue("copy_ticker_placeholder_text");
  const { currentSidebarType } = useSidebarNavigation();
  const sidebarWidth = (useUserPreference("ui_sidebar_width") as number) || (DEFAULT_SIDEBAR_WIDTH as number);
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const boxShadowColor = useColorModeValue("0, 0, 0", "255, 255, 255");
  const messageIconColor = useColorModeValue("gray.500", "gray.200");

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(downloadTags());
  }, [dispatch]);

  useEffect(() => {
    if (conversationId.length > 0 && !hasConversation) {
      dispatch(downloadConversationById({ conversationId: conversationId }));
    }
  }, [hasConversation, conversationId, dispatch]);

  useEffect(() => {
    setConversationId((prevConversationId: string) => {
      const newConversationId = uuid();
      const nextConversationId = prevConversationId || newConversationId;

      return nextConversationId;
    });
  }, [setConversationId]);

  const handleToggleShowConversationMessages = useCallback(() => {
    setShowConversationMessages(!showConversationMessages);
  }, [setShowConversationMessages, showConversationMessages]);

  if (!isConversationOpen) return null;

  return (
    <Center
      position="fixed"
      bottom="0"
      left={currentSidebarType === "hidden" ? 0 : `${sidebarWidth}px`}
      width="100%"
      maxWidth={`calc(100vw - ${currentSidebarType === "hidden" ? 0 : sidebarWidth}px)`}
      zIndex="10"
      pointerEvents="none"
      pb="1rem">
      <Box
        width={isMobile ? "100%" : "40rem"}
        borderRadius="1rem"
        backgroundColor={bgInputColor}
        boxShadow={`0 4px 20px rgba(${boxShadowColor}, 0.25)`}
        pointerEvents="auto">
        <InputBarDropzone
          borderRadius={"1rem"}
          attachmentId="conversation"
          display="flex"
          flexGrow={1}
          flexDirection="column"
          background={bgInputColor}
          minWidth={0}
          maxH={"85vh"}
          height="100%">
          {showConversationMessages && <ConversationContent conversationId={currentConversationId} />}
          <Box position={"relative"} width="100%" px="1rem" pt="1.5rem" pb="1.5rem" className="conversation-dialog-autocomplete-input">
            <Tooltip label={showConversationMessages ? "Close messages" : "Open messages"} placement="top">
              <Box
                className="ch-conversation-dialog-toggle-messages"
                onClick={handleToggleShowConversationMessages}
                backgroundColor={bgInputColor}
                position={"absolute"}
                top="-1rem"
                right="1rem"
                px="10px"
                pt="10px"
                borderRadius="full"
                zIndex={1}
                boxShadow={showConversationMessages ? "unset" : `0 -6px 9px -6px rgba(${boxShadowColor}, 0.2)`}>
                <Icon cursor={"pointer"} color={messageIconColor} as={BiChat} boxSize="1.2rem" />
              </Box>
            </Tooltip>
            <ConversationDialogInput
              setListType="commands"
              minWidth="100%"
              minWidthList="100%"
              conversationId={currentConversationId}
              inputId={"conversation-input"}
              className="ch-autocomplete-input-conversation-dialog ch-autocomplete-input"
              initialText={projectId ? "Ask me a question about this project" : copyTickerPlaceholder}
              renderRole="cli"
            />
          </Box>
        </InputBarDropzone>
      </Box>
    </Center>
  );
};
